import * as React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { AUTH_TEXT_SECONDARY, AUTH_ERROR, AUTH_INPUT_BORDER, BRAND_BLUE } from "../../theme/appTheme";
import { useTranslation } from "../../i18n/LanguageContext";

const MAX_FILES = 5;
const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["application/pdf", "image/png", "image/jpeg"];

function formatSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Staging list for the therapist-application certificate uploads - collects
// plain File objects locally (no network call here) so it can be used both
// before an account exists (registration) and after (Account Settings); the
// caller uploads them via therapistApplyService at actual submit time.
function DocumentUploadList({ files, onChange, disabled = false }) {
  const { t } = useTranslation();
  const inputRef = React.useRef(null);
  const [localError, setLocalError] = React.useState("");
  const [isDragging, setIsDragging] = React.useState(false);
  const atLimit = files.length >= MAX_FILES;
  const dropDisabled = disabled || atLimit;

  const handleFilesSelected = (fileList) => {
    setLocalError("");
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    const accepted = [];
    let error = "";

    for (const file of incoming) {
      if (files.length + accepted.length >= MAX_FILES) {
        error = t("auth.documentUpload.tooMany", { max: MAX_FILES });
        break;
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        error = t("auth.documentUpload.invalidType", { name: file.name });
        continue;
      }
      if (file.size > MAX_SIZE_BYTES) {
        error = t("auth.documentUpload.tooLarge", { name: file.name });
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length) onChange([...files, ...accepted]);
    if (error) setLocalError(error);
  };

  const handleRemove = (index) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dropDisabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (dropDisabled) return;
    handleFilesSelected(e.dataTransfer.files);
  };

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={(e) => {
          handleFilesSelected(e.target.files);
          e.target.value = "";
        }}
      />

      <Box
        onClick={() => !dropDisabled && inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: `2px dashed ${isDragging ? BRAND_BLUE : AUTH_INPUT_BORDER}`,
          borderRadius: 2,
          bgcolor: isDragging ? "rgba(0,88,165,0.05)" : "#F9FAFB",
          px: 2,
          py: 3,
          textAlign: "center",
          cursor: dropDisabled ? "not-allowed" : "pointer",
          opacity: dropDisabled ? 0.6 : 1,
          transition: "border-color 0.15s, background-color 0.15s",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "#EAF1FA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 1,
          }}
        >
          <AddIcon sx={{ fontSize: 20, color: BRAND_BLUE }} />
        </Box>
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151" }}>
          {t("auth.documentUpload.clickOrDrag")}
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: "0.6875rem", color: AUTH_TEXT_SECONDARY }}>
          {t("auth.documentUpload.hint", { count: files.length, max: MAX_FILES })}
        </Typography>
      </Box>

      {localError && (
        <Typography variant="caption" sx={{ color: AUTH_ERROR, mt: 0.75, display: "block" }}>
          {localError}
        </Typography>
      )}

      {files.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1.5 }}>
          {files.map((file, index) => (
            <Box
              key={`${file.name}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.875,
                border: `1px solid ${AUTH_INPUT_BORDER}`,
                borderRadius: 1.5,
              }}
            >
              <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#6b7280" }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: "0.8125rem", color: "#111827" }}>
                  {file.name}
                </Typography>
                <Typography sx={{ fontSize: "0.6875rem", color: AUTH_TEXT_SECONDARY }}>
                  {formatSize(file.size)}
                </Typography>
              </Box>
              <IconButton size="small" disabled={disabled} onClick={() => handleRemove(index)}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default DocumentUploadList;
